import React, { useEffect, useState, useRef } from "react";
import { useRouter } from "next/router";
import styles from "./index.module.scss";
import Tooltip from "@/components/ToolTip";
import MapView from "@components/CustomComponents/LocatorStore/Map";
import SearchResult from "@components/CustomComponents/LocatorStore/SearchResult";
import { renderRichText } from "@/utils/formatters";
import { useLocale } from "@/hooks/useLocale";
import { useContentfulTranslations } from "@/utils/contentfulTranslations";

const getStoreLocationData = (locale, MAP_LABELS) => {
  const labels = MAP_LABELS?.[locale] || MAP_LABELS?.en;
  let checkboxes = Object.values(labels.checkbox).map(item => ({
    label: item.name,
    name: item.value,
  }));
  return {
    heading: "Find a Store",
    buttonLabel: labels.storelocatorButtonLabel,
    inputField: true,
    checkBoxList: checkboxes,
    showToolTip: true,
    showMap: true,
  };
};

const KohlerCheckBox = ({ name, value, checked, onChange }) => {
  return (
    <div className="cursor-pointer form-options">
      <div className="checkbox-inline flex gap-3">
        <input
          className="rounded border border-gray-300 accent-gray-300 custom-tick cursor-pointer"
          value={value}
          id="koh-exp-center"
          type="checkbox"
          checked={checked}
          onChange={onChange}
        />
        <Tooltip text={name} showArrow={true}>
          <div className="koh-dotted-span">{name}</div>
        </Tooltip>
      </div>
    </div>
  );
};

const StoreLocator = ({ locatorData = {} }) => {
  const defaultLocale = useLocale();
  const { MAP_LABEL } = useContentfulTranslations();
  const apiKey = process.env.NEXT_PUBLIC_MAP_API_KEY;
  const findStoreApi = process.env.NEXT_PUBLIC_FIND_STORE_API;
  const locationApi = process.env.NEXT_PUBLIC_LOCATION_API;
  const storeLocationData = getStoreLocationData(defaultLocale, MAP_LABEL);
  const {
    buttonLabel = "",
    checkBoxList = [],
    // showSorryText = true,
  } = storeLocationData;

  const { heading = "", sorryText = "" } = locatorData || {};
  const searchBoxRef = useRef(null);
  const [selected, setSelected] = useState(
    checkBoxList?.map(item => item.name) || []
  );
  const [showSorryText, setShowSorryText] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [location, setLocation] = useState(null);
  const [query, setQuery] = useState("");
  const [searchText, setSearchText] = useState("");
  const [result, setResult] = useState(null);

  const handleToggle = item => {
    setSelected(
      prev =>
        prev.includes(item)
          ? prev.filter(i => i !== item) // uncheck
          : [...prev, item] // check
    );
  };

  const checkAllBoxesOnSearch = () => {
    const allItems = checkBoxList.map(item => item.name);

    if (selected.length !== allItems.length) {
      setSelected(allItems);
    }
  };

  const searchLocation = async () => {
    checkAllBoxesOnSearch();
    if (!query) return;
    setSearchText(query);

    try {
      const res = await fetch(
        `${locationApi}?q=${encodeURIComponent(
          query
        )}&key=${process.env.NEXT_PUBLIC_MAP_KEY}`
      );

      const data = await res.json();
      const location = data.resourceSets[0]?.resources[0];

      if (!location) {
        setShowResults(false);
        setShowSorryText(true);
        return;
      }

      const lat = location.point.coordinates[0];
      const lng = location.point.coordinates[1];

      const locationData = {
        name: location.name,
        latitude: lat,
        longitude: lng,
      };
      setLocation(locationData);
      let storeLsit = await fetchStores({ latitude: lat, longitude: lng });
      setResult(storeLsit);
      setHasSearched(true);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchStores = async ({ latitude, longitude }) => {
    if (!latitude || !longitude) {
      console.error("Missing latitude or longitude for fetchStores");
      return null;
    }
    try {
      const url = `${findStoreApi}?$select=*,__Distance&$top=50&$inlinecount=allpages&$format=json&spatialFilter=nearby(${latitude},${longitude},400)&key=${apiKey}`;
      const response = await fetch(url);
      const data = await response.json();
      setShowResults(true);
      return data;
    } catch (error) {
      console.error("fetchStores error:", error);
      setShowResults(false);
      return null;
    }
  };

  useEffect(() => {
    const { __count = 0 } = result?.d || {};
    if (!hasSearched) return;
    if (__count > 0) {
      setShowResults(true);
      setShowSorryText(false);
    } else {
      setShowSorryText(true);
      setShowResults(false);
    }
  }, [location, result]);

  const noResultText = renderRichText(sorryText);
  return (
    <>
      <div className={styles.wrapper}>
        <section className="c-koh-store-locator">
          <div className="containers-part">
            <div className="koh-map-form-inner-content">
              <p className="text-center koh-map-main-header w-full">
                {heading}
              </p>
            </div>
            <div className="container-wrapper flex flex-col items-start justify-center gap-[15px] mx-auto">
              <div className="row text-center flex items-center justify-center gap-1 w-full flex-col md:flex-row ">
                <div className="mt-2  w-full md:w-9/12">
                  <div className=" koh-search-text flex items-center rounded-md bg-white pl-3 outline-1 -outline-offset-1 outline-gray-300">
                    <input
                      id="koh-search-text"
                      type="text"
                      ref={searchBoxRef}
                      name="search-text"
                      placeholder="City, State/Province or Postal Code"
                      className="block min-w-0 grow py-1.5 pr-3 pl-1 text-base text-gray-900 placeholder:text-gray-400 focus:outline-none sm:text-sm/6 bg-white focus:bg-white autofill:bg-white"
                      defaultValue={query}
                      onChange={e => setQuery(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === "Enter") {
                          searchLocation();
                        }
                      }}
                    />
                  </div>
                </div>
                <div className="mt-2 w-full md:w-3/12 cursor-pointer">
                  <button
                    type="button"
                    onClick={searchLocation}
                    className="btn btn-primary koh-store-search-btn cursor-pointer  !font-[HelveticaRoman,_Helvetica,_sans-serif]"
                  >
                    {buttonLabel}
                  </button>
                </div>
              </div>
              <div className="koh-map-check">
                <div className="cursor-pointer form-group flex flex-wrap gap-x-[15px] flex-col md:flex-row gap-5 md:gap-x-[15px]">
                  {checkBoxList.map((item, index) => (
                    <KohlerCheckBox
                      key={index}
                      name={item?.label}
                      value={item?.name}
                      checked={selected.includes(item?.name)}
                      onChange={() => handleToggle(item?.name)}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
          <hr></hr>
          <div className="koh-no-result">
            {showSorryText ? (
              <p className="text-center-info font-extrabold text-3xl text-[#000] text-center mt-10 mb-5  !font-[HelveticaRoman,_Helvetica,_sans-serif]">
                {renderRichText(sorryText)}
              </p>
            ) : null}
          </div>
        </section>
      </div>
      <div
        className={`koh-map-wrapper  relative z-0 mb-5 ${showResults ? "mx-5" : ""}`}
      >
        {showResults ? (
          <SearchResult
            selected={selected}
            searchText={searchText}
            result={result}
            location={location}
            showResults={showResults}
            locale={defaultLocale}
            sorryText={noResultText}
          />
        ) : storeLocationData.showMap ? (
          <MapView location={location} />
        ) : null}
      </div>
    </>
  );
};

StoreLocator.propTypes = {};

export default StoreLocator;
